import { type PermissionStatus } from '@apps/web/features/phoebe_chat_v3/registry/definitions/tool_call';
import { type PermissionOutcome } from '@apps/web/features/phoebe_chat_v3/registry/definitions/tool_call_approval_views';
import { type CommunicationOutcome } from '@apps/web/features/phoebe_chat_v3/registry/types';
import { type ReceiptStatus } from '@apps/web/features/phoebe_chat_v3/ui/primitives/compact_receipt';

type CommunicationProgress =
    | Readonly<{
          readonly kind: 'approval';
          readonly status: PermissionStatus;
          readonly permissionOutcome: PermissionOutcome;
      }>
    | Readonly<{
          readonly kind: 'no_approval';
          readonly executionOutcome: PermissionOutcome;
      }>;

export type CommunicationReceiptState =
    | Readonly<{ readonly kind: 'pending' }>
    | Readonly<{ readonly kind: 'approved' }>
    | Readonly<{ readonly kind: 'rejected' }>
    | Readonly<{ readonly kind: 'failed' }>
    | Readonly<{ readonly kind: 'sent' }>
    | Readonly<{ readonly kind: 'unconfirmed' }>
    | Readonly<{ readonly kind: 'partial' }>
    | Readonly<{ readonly kind: 'no_contact' }>
    | Readonly<{ readonly kind: 'interrupted' }>;

// Permission is the gate; a completed tool call alone is not a delivery result.
export function communicationReceiptState(
    progress: CommunicationProgress,
    communicationOutcome: CommunicationOutcome | undefined,
): CommunicationReceiptState {
    if (progress.kind === 'approval') {
        if (progress.status === 'waiting') {
            return { kind: 'pending' };
        }
        if (progress.status !== 'granted') {
            return { kind: 'rejected' };
        }
    } else if (progress.executionOutcome === 'pending') {
        return { kind: 'pending' };
    }

    switch (communicationOutcome) {
        case 'succeeded':
            return {
                kind: progress.kind === 'approval' ? 'approved' : 'sent',
            };
        case 'partial':
            return { kind: 'partial' };
        case 'no_contact':
            return { kind: 'no_contact' };
        case 'interrupted':
            return { kind: 'interrupted' };
        case undefined:
            if (progress.kind === 'approval') {
                return {
                    kind:
                        progress.permissionOutcome === 'failed'
                            ? 'failed'
                            : 'approved',
                };
            }
            return {
                kind:
                    progress.executionOutcome === 'failed'
                        ? 'failed'
                        : 'unconfirmed',
            };
        default: {
            const exhaustiveOutcome: never = communicationOutcome;
            return exhaustiveOutcome;
        }
    }
}

export function communicationReceiptCopy(
    kind: 'sms' | 'voice',
    state: CommunicationReceiptState,
    recipientCount: number,
): Readonly<{
    readonly tone: 'approved' | 'rejected';
    readonly status: ReceiptStatus;
    readonly statusLabel?: string;
    readonly title: string;
    readonly detail?: string;
}> | null {
    switch (state.kind) {
        case 'pending':
            return null;
        case 'approved':
            return {
                statusLabel: 'Approved',
                tone: 'approved',
                status: 'succeeded',
                title:
                    kind === 'voice'
                        ? 'Call approved'
                        : recipientCount === 1
                          ? 'Text approved'
                          : 'Texts approved',
            };
        case 'rejected':
            return {
                tone: 'rejected',
                status: 'denied',
                statusLabel: 'Rejected',
                title: kind === 'sms' ? 'Text message' : 'Phone call',
            };
        case 'failed':
            return {
                tone: 'rejected',
                status: 'failed',
                title: kind === 'sms' ? 'Send failed' : 'Call failed',
            };
        case 'sent':
            return {
                tone: 'approved',
                status: 'succeeded',
                title: kind === 'sms' ? 'Texts sent' : 'Call queued',
            };
        case 'unconfirmed':
            return {
                tone: 'rejected',
                status: 'failed',
                title:
                    kind === 'sms'
                        ? 'Delivery unconfirmed'
                        : 'Call status unconfirmed',
            };
        case 'partial':
            return {
                tone: 'rejected',
                status: 'failed',
                title: 'Some texts sent',
                detail: 'Some recipients were not contacted',
            };
        case 'no_contact':
            return {
                tone: 'rejected',
                status: 'failed',
                title: kind === 'sms' ? 'No texts sent' : 'No call placed',
                detail: 'No caregiver was contacted',
            };
        case 'interrupted':
            return {
                tone: 'rejected',
                status: 'failed',
                title: 'Sending interrupted',
                detail: 'Some recipients were not confirmed or contacted',
            };
        default: {
            const exhaustiveState: never = state;
            return exhaustiveState;
        }
    }
}
